import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Routes from 'constants/Routes';
import useModalStack from 'contexts/modal/useModalStack';
import styled from 'styled-components';
import DetailedCaseFormProvider from './DetailedCase/DetailedCaseFormProvider';
import DetailedCaseProvider from './DetailedCase/DetailedCaseProvider';
import { CaseModal } from 'pages/Case';

const TextLink = styled.a`
  color: inherit;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    text-decoration-style: solid;
  }
`;

/**
 * Opens a case inline if clicked directly, but in a new tab if Ctrl or Cmd is held.
 *
 * Without children it renders the open-in-new icon. With children (e.g. a registration number)
 * the children themselves become the link, and the click does not bubble — the text often sits
 * inside something clickable of its own.
 */
const CaseOpenLink: React.FC<{ caseID: number; children?: React.ReactNode }> = ({
  caseID,
  children,
}) => {
  const modalStack = useModalStack();
  const openInline = () =>
    modalStack.push(
      <DetailedCaseProvider caseId={caseID}>
        <DetailedCaseFormProvider>
          <CaseModal onClose={() => modalStack.pop()} />
        </DetailedCaseFormProvider>
      </DetailedCaseProvider>
    );
  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      window.open(
        `${Routes.search.index}/${caseID}`,
        caseID.toString() ?? '_blank'
      );
    } else {
      openInline();
    }
  };

  if (children !== undefined) {
    return (
      <TextLink
        href={`${Routes.search.index}/${caseID}`}
        onClick={(e) => {
          e.stopPropagation();
          // Ctrl/Cmd-klick och mittenklick får webbläsarens vanliga ny-flik via href.
          if (e.ctrlKey || e.metaKey || e.shiftKey) return;
          e.preventDefault();
          openInline();
        }}
        title="Öppna ärendet (Ctrl/Cmd-klick: ny flik)"
      >
        {children}
      </TextLink>
    );
  }

  return (
    <FontAwesomeIcon
      onClick={handleClick}
      icon={faArrowUpRightFromSquare}
      fontSize="18px"
      style={{ cursor: 'pointer' }}
    />
  );
};

export default CaseOpenLink;
